if (!customElements.get('menu-showcase')) {
  class MenuShowcase extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-menu-tab-button]'));
      this.panels = Array.from(this.querySelectorAll('[data-menu-tab-panel]'));
      this.tabsWrap = this.querySelector('[data-menu-tabs-wrap]');
      this.tabs = this.querySelector('[data-menu-tabs]');
      this.searchInput = this.querySelector('[data-menu-search-input]');
      this.filterButtons = Array.from(this.querySelectorAll('[data-menu-filter-button]'));
      this.mobileTabBehavior = this.dataset.mobileTabBehavior || 'scroll_with_arrows';
      this.activeFilters = {
        dietary: '',
        feature: '',
      };

      this.onResize = this.updateScrollState.bind(this);
      this.onTabScroll = this.updateScrollState.bind(this);
      this.onSearchInput = this.applyFilters.bind(this);

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.activateTab(button.dataset.menuTabButton));
      });

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => this.handleFilterClick(button));
      });

      if (this.tabsWrap && this.tabs) {
        this.tabs.addEventListener('scroll', this.onTabScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        requestAnimationFrame(this.updateScrollState.bind(this));
      }

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.onSearchInput);
      }

      const activeButton = this.buttons.find((button) => button.classList.contains('is-active')) || this.buttons[0];
      if (activeButton) {
        this.activateTab(activeButton.dataset.menuTabButton);
      } else {
        this.applyFilters();
      }
    }

    disconnectedCallback() {
      if (this.tabs) {
        this.tabs.removeEventListener('scroll', this.onTabScroll);
      }

      window.removeEventListener('resize', this.onResize);

      if (this.searchInput) {
        this.searchInput.removeEventListener('input', this.onSearchInput);
      }
    }

    activateTab(tabId) {
      this.buttons.forEach((button) => {
        const isActive = button.dataset.menuTabButton === tabId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive.toString());
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.menuTabPanel === tabId;
        panel.hidden = !isActive;
      });

      const activeButton = this.buttons.find((button) => button.dataset.menuTabButton === tabId);
      if (activeButton && this.tabs && this.mobileTabBehavior === 'scroll_with_arrows') {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      this.applyFilters();
    }

    handleFilterClick(button) {
      const group = button.dataset.menuFilterGroup;
      const value = button.dataset.menuFilterValue || '';

      this.filterButtons
        .filter((candidate) => candidate.dataset.menuFilterGroup === group)
        .forEach((candidate) => {
          candidate.classList.toggle('is-active', candidate === button);
        });

      this.activeFilters[group] = value;
      this.applyFilters();
    }

    applyFilters() {
      const query = (this.searchInput?.value || '').trim().toLowerCase();
      const dietaryFilter = this.activeFilters.dietary;
      const featureFilter = this.activeFilters.feature;

      this.panels.forEach((panel) => {
        const items = Array.from(panel.querySelectorAll('[data-menu-item]'));
        let visibleCount = 0;

        items.forEach((item) => {
          const searchText = item.dataset.menuSearch || '';
          const dietaryTokens = (item.dataset.menuDietary || '').split('|').filter(Boolean);
          const featureTokens = (item.dataset.menuFeatures || '').split('|').filter(Boolean);

          const matchesSearch = !query || searchText.includes(query);
          const matchesDietary = !dietaryFilter || dietaryTokens.includes(dietaryFilter);
          const matchesFeature = !featureFilter || featureTokens.includes(featureFilter);
          const shouldShow = matchesSearch && matchesDietary && matchesFeature;

          item.hidden = !shouldShow;
          item.classList.toggle('is-filter-hidden', !shouldShow);

          if (shouldShow) {
            visibleCount += 1;
          }
        });

        panel.querySelectorAll('[data-menu-group]').forEach((group) => {
          const groupHasVisibleItems = Array.from(group.querySelectorAll('[data-menu-item]')).some((item) => !item.hidden);
          group.hidden = !groupHasVisibleItems;
        });

        const emptyState = panel.querySelector('[data-menu-empty-state]');
        if (emptyState) {
          emptyState.hidden = visibleCount > 0;
        }
      });
    }

    updateScrollState() {
      if (!this.tabsWrap || !this.tabs) return;

      if (this.mobileTabBehavior !== 'scroll_with_arrows') {
        this.tabsWrap.classList.remove('is-scrollable', 'can-scroll-left', 'can-scroll-right');
        return;
      }

      const hasOverflow = this.tabs.scrollWidth - this.tabs.clientWidth > 8;
      const maxScrollLeft = this.tabs.scrollWidth - this.tabs.clientWidth;
      const canScrollLeft = this.tabs.scrollLeft > 8;
      const canScrollRight = this.tabs.scrollLeft < maxScrollLeft - 8;

      this.tabsWrap.classList.toggle('is-scrollable', hasOverflow);
      this.tabsWrap.classList.toggle('can-scroll-left', hasOverflow && canScrollLeft);
      this.tabsWrap.classList.toggle('can-scroll-right', hasOverflow && canScrollRight);
    }
  }

  customElements.define('menu-showcase', MenuShowcase);
}
